/*
  THE TEN THEMES' ARTWORK, rebrand step 6, 17 September 2026.

  **Ported from `docs/brand/reference/onhand-themes-approved-v1.html`**, which
  is the approved reference and is never edited. The shapes, the colours, the
  sizes, the tempo (one event about every 2.6 seconds, moves of about 1.1
  seconds) and the parallax (up to 26 by 14 pixels) are that file's, moved into
  a module the app can mount and unmount. Lightweight illustrated depth: HTML,
  CSS and SVG, no 3D library (real 3D is the sign-in screen's, step 7).

  What the app adds around it, from the rebrand's step 6 instructions:

  - **Nothing moves when the device asks for less motion.** No timers start, no
    animation classes are given out, and the stylesheet stops the rest.
  - **No parallax on a touch device**, and none from a touch pointer on a
    device that has both.
  - **Everything pauses when the browser tab is hidden**, and starts again when
    it comes back, so a sheet left open in another tab costs nothing.
  - **It is drawn behind everything** and is hidden from screen readers; the
    page above it is untouched.

  The colours come from the page: `--bg` is the theme's base tint, which
  `src/theme/brandThemes.css` maps from the brand tokens.
*/

const NS = 'http://www.w3.org/2000/svg';
const BOX = 'M8 17L12 26V44A8 8 0 0 0 20 52H44A8 8 0 0 0 52 44V26L56 17M12 26H52';

/* The tempo of the reference: one event about every 2.6 seconds. */
export const BEAT_MS = 2600;
export const PARALLAX = { x: 26, y: 14 };

const rand = (a, b) => a + Math.random() * (b - a);
const pick = arr => arr[Math.floor(Math.random() * arr.length)];

const lighten = (hex, amt) => {
  const n = parseInt(hex.slice(1), 16);
  let r = n >> 16, g = (n >> 8) & 255, b = n & 255;
  r = Math.round(r + (255 - r) * amt); g = Math.round(g + (255 - g) * amt); b = Math.round(b + (255 - b) * amt);
  return 'rgb(' + r + ',' + g + ',' + b + ')';
};
const darken = (hex, amt) => {
  const n = parseInt(hex.slice(1), 16);
  const r = n >> 16, g = (n >> 8) & 255, b = n & 255;
  return 'rgb(' + Math.round(r * (1 - amt)) + ',' + Math.round(g * (1 - amt)) + ',' + Math.round(b * (1 - amt)) + ')';
};

/* Is this a device where a pointer can hover? Parallax is for those only. */
export function canParallax(win = typeof window === 'undefined' ? null : window) {
  if (!win || !win.matchMedia) return false;
  try { return win.matchMedia('(pointer: fine)').matches; } catch { return false; }
}

export function prefersReducedMotion(win = typeof window === 'undefined' ? null : window) {
  if (!win || !win.matchMedia) return false;
  try { return win.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

/*
  Draw a theme's art into `host` and keep it moving. Returns a function that
  stops everything and leaves the host empty.
*/
export function mountArt(host, options = {}) {
  if (!host) return () => {};
  const win = options.window || window;
  const doc = host.ownerDocument || document;
  const reduceMotion = options.reduceMotion !== undefined ? options.reduceMotion : prefersReducedMotion(win);
  const parallax = options.parallax !== undefined ? options.parallax : canParallax(win);
  let theme = options.theme || 'plain';
  let dark = !!options.dark;

  let timers = [];
  let buildId = 0;
  let u = 1, W = 0, H = 0;
  let paused = false;
  let resizeTimer = null;
  let stopped = false;

  const X = x => 40 + W / 2 + (x - 720) * u;
  const Y = y => 40 + H / 2 + (y - 450) * u;
  const shadowCol = () => (dark ? 'rgba(0,0,0,0.55)' : 'rgba(11,52,86,0.24)');

  /* Every repeating event goes through here, so the whole lot can be paused. */
  const beats = [];
  const every = (ms, fn) => {
    if (reduceMotion) return;
    beats.push({ ms, fn });
    if (!paused) timers.push(win.setInterval(fn, ms));
  };
  const later = (ms, fn) => { const t = win.setTimeout(fn, ms); timers.push(t); return t; };
  const clearTimers = () => { timers.forEach(t => { win.clearInterval(t); win.clearTimeout(t); }); timers = []; };

  function div(parent, cls, style, html) {
    const d = doc.createElement('div');
    if (cls) d.className = cls;
    if (style) d.setAttribute('style', style);
    if (html) d.innerHTML = html;
    parent.appendChild(d);
    return d;
  }
  function layer(depth, style) { const d = div(host, 'lyr', style || ''); d.dataset.depth = depth; return d; }
  function svgLayer(depth, inner, style) {
    const l = layer(depth, style);
    l.innerHTML = '<svg class="full" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice">' + inner + '</svg>';
    return l;
  }
  function svgAdd(parent, tag, attrs) {
    const e = doc.createElementNS(NS, tag);
    Object.keys(attrs).forEach(k => e.setAttribute(k, attrs[k]));
    parent.appendChild(e);
    return e;
  }
  const animate = (el, frames, opts) => (el.animate ? el.animate(frames, opts) : null);

  const P = () => (dark
    ? { a1: '#0B5E98', a2: '#0A4A77', a3: '#0B3456', gold: '#D6961A', gold2: '#B67A12', blue: '#1B7FC3', navy: '#0B3456', paper: '#0E2A45' }
    : { a1: '#9CC5E6', a2: '#C6DDF0', a3: '#D3E6F4', gold: '#EDAF24', gold2: '#F2C556', blue: '#3F91CC', navy: '#0B3456', paper: '#F4F8FB' });

  function chip(parent, cx, cy, size, color, rot, opts) {
    const s = size * u;
    const wrap = div(parent, 'chip' + (opts && opts.drift && !reduceMotion ? ' drift' : ''),
      'left:' + (X(cx) - s / 2) + 'px;top:' + (Y(cy) - s / 2) + 'px;width:' + s + 'px;height:' + s + 'px;'
      + (opts && opts.drift ? '--dx:' + rand(-30, 30) * u + 'px;--dy:' + rand(-26, 26) * u + 'px;--dr:' + rand(-9, 9) + 'deg;--dur:' + rand(13, 20) + 's;' : ''));
    const inner = div(wrap, '', 'position:absolute;inset:0;transform:rotate(' + rot + 'deg)');
    const hopper = div(inner, '', 'position:absolute;inset:0;--hopY:' + (-22 * u) + 'px');
    div(hopper, '', 'position:absolute;inset:0;border-radius:22%;background:linear-gradient(160deg,' + lighten(color, dark ? 0.12 : 0.28) + ' 0%,' + color + ' 55%,' + darken(color, 0.12) + ' 100%);box-shadow:inset 0 ' + 2 * u + 'px 0 rgba(255,255,255,' + (dark ? 0.15 : 0.45) + '), 0 ' + 22 * u + 'px ' + 34 * u + 'px ' + (-10 * u) + 'px ' + shadowCol() + ', 0 ' + 4 * u + 'px ' + 8 * u + 'px ' + (dark ? 'rgba(0,0,0,0.35)' : 'rgba(11,52,86,0.12)'));
    wrap.hop = () => { hopper.classList.remove('hop'); void hopper.offsetWidth; hopper.classList.add('hop'); };
    return wrap;
  }

  function makeTruck(parent) {
    const tw = 104 * u, th = 52 * u;
    const truck = div(parent, 'abs', 'left:0;top:0;width:' + tw + 'px;height:' + th + 'px;transform-origin:50% 90%;will-change:transform;filter:drop-shadow(0 ' + 6 * u + 'px ' + 8 * u + 'px ' + shadowCol() + ')');
    div(truck, 'abs', 'left:0;top:0;width:72%;height:78%;border-radius:' + 6 * u + 'px;background:linear-gradient(180deg,#2A89CF,#0E72B8);box-shadow:inset 0 ' + 2 * u + 'px 0 rgba(255,255,255,0.3)', '<svg viewBox="0 0 64 64" style="position:absolute;left:34%;top:18%;width:32%;height:62%"><rect x="20" y="10" width="24" height="24" rx="4.5" transform="rotate(-8 32 22)" fill="#EDAF24"/><path d="' + BOX + '" fill="none" stroke="#FFFFFF" stroke-width="8" stroke-linecap="round" stroke-linejoin="round"/></svg>');
    div(truck, 'abs', 'left:74%;top:22%;width:26%;height:56%;border-radius:' + 4 * u + 'px ' + 10 * u + 'px ' + 4 * u + 'px ' + 4 * u + 'px;background:#0B3456', '<div style="position:absolute;left:18%;top:14%;width:52%;height:40%;border-radius:3px;background:#9CC7E8"></div>');
    [16, 80].forEach(pc => div(truck, 'abs', 'left:' + pc + '%;bottom:0;width:' + 16 * u + 'px;height:' + 16 * u + 'px;border-radius:50%;background:#1C2733;box-shadow:inset 0 0 0 ' + 4 * u + 'px #36495B'));
    truck.size = { w: tw, h: th };
    return truck;
  }

  const THEMES = {
    open() {
      const p = P();
      layer(0.15, 'background: linear-gradient(180deg, ' + (dark ? 'rgba(27,127,195,0.10)' : 'rgba(255,255,255,0.5)') + ', rgba(0,0,0,0) 45%), var(--bg);');
      const boxes = svgLayer(0.45, '<g transform="translate(820 250) scale(10)"><path d="' + BOX + '" fill="none" stroke="' + p.a1 + '" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></g>'
        + '<g transform="translate(-110 560) scale(6)"><path d="' + BOX + '" fill="none" stroke="' + p.a2 + '" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></g>'
        + '<g transform="translate(1210 -80) scale(5)"><path d="' + BOX + '" fill="none" stroke="' + p.a2 + '" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></g>');
      boxes.firstChild.style.filter = 'drop-shadow(0 ' + 16 * u + 'px ' + 18 * u + 'px ' + shadowCol() + ')';
      const front = layer(1);
      const chips = [[1165, 175, 210, p.gold, -8], [968, 168, 96, p.gold2, 10], [1070, 770, 140, p.blue, -14], [1340, 600, 80, p.gold, 12], [250, 190, 72, p.blue, 8], [520, 830, 92, p.gold2, -6], [720, 120, 54, p.gold, 16]]
        .map(c => chip(front, c[0], c[1], c[2], c[3], c[4], { drift: true }));
      every(BEAT_MS, () => pick(chips).hop());
    },

    shelves() {
      const wall = layer(0.25, 'background: linear-gradient(180deg, rgba(255,255,255,' + (dark ? '0.03' : '0.28') + '), rgba(0,0,0,0) 35%, rgba(0,0,0,' + (dark ? '0.25' : '0.06') + ') 100%), repeating-linear-gradient(90deg, rgba(0,0,0,0) 0 118px, ' + (dark ? 'rgba(0,0,0,0.35)' : 'rgba(60,70,80,0.10)') + ' 118px 120px, ' + (dark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.55)') + ' 120px 122px), var(--bg);');
      void wall;
      const rack = layer(1);
      const Wr = W + 80, Hr = H + 80;
      const floorH = Math.round(Hr * 0.13), rackTop = 30, rackBottom = Hr - floorH, levels = 5;
      const levelH = (rackBottom - rackTop) / levels, bay = Math.max(220, Math.round(levelH * 2.1));
      div(rack, 'il-floor', 'height:' + floorH + 'px');
      const kraft = dark ? ['#8E6E4B', '#86664A', '#977656', '#7E5F40', '#916F4E'] : ['#C29663', '#B88B56', '#CDA572', '#B0834C', '#C69D69'];
      const bands = ['#EDAF24', '#0E72B8', '#0B3456', '#EDAF24'];
      const slots = [];
      const place = (sl, arriving) => {
        const el = div(rack, 'il-box cardboard' + (arriving ? ' arriving' : ''), 'background:' + pick(kraft) + ';left:' + sl.x + 'px;width:' + sl.w + 'px;height:' + sl.h + 'px;top:' + (sl.bottom - sl.h) + 'px', '<div class="il-tape"></div><div class="il-label"><i style="background:' + pick(bands) + '"></i><b></b></div>');
        sl.el = el;
        return el;
      };
      for (let l = 0; l < levels; l++) {
        const beamY = rackTop + levelH * (l + 1);
        div(rack, 'il-shade', 'top:' + (rackTop + levelH * l + 15) + 'px;height:' + levelH * 0.45 + 'px');
        div(rack, 'il-beam', 'top:' + (beamY - 15) + 'px');
        for (let bx = 0; bx < Wr; bx += bay) {
          let x = bx + 18;
          for (;;) {
            const w = Math.round(rand(bay * 0.22, bay * 0.42));
            if (x + w > bx + bay - 14) break;
            const sl = { x, w, h: Math.round(rand(levelH * 0.4, levelH * 0.78)), bottom: beamY - 15, el: null, busy: false };
            if (Math.random() > 0.07) place(sl, false);
            slots.push(sl);
            x += w + Math.round(rand(6, 18));
          }
        }
      }
      for (let bx = 0; bx < Wr + bay; bx += bay) div(rack, 'il-post', 'left:' + (bx - 7) + 'px;top:' + rackTop + 'px;height:' + (rackBottom - rackTop + 6) + 'px');
      every(BEAT_MS, () => {
        const vis = slots.filter(s => !s.busy && s.x > 60 && s.x < W - 20);
        const full = vis.filter(s => s.el), empty = vis.filter(s => !s.el);
        if (empty.length && Math.random() < 0.45) {
          const sl = pick(empty); sl.busy = true;
          const el = place(sl, true); void el.offsetWidth; el.classList.remove('arriving');
          later(1200, () => { sl.busy = false; });
        } else if (full.length) {
          const sl = pick(full); sl.busy = true;
          sl.el.classList.add('away');
          later(1150, () => {
            if (sl.el) sl.el.remove();
            sl.el = null;
            later(rand(900, 2400), () => {
              if (Math.random() < 0.8) {
                const el = place(sl, true); void el.offsetWidth; el.classList.remove('arriving');
                later(1200, () => { sl.busy = false; });
              } else sl.busy = false;
            });
          });
        }
      });
    },

    blueprint() {
      const p = P();
      const line1 = dark ? 'rgba(116,176,220,0.10)' : 'rgba(14,114,184,0.10)';
      const line2 = dark ? 'rgba(116,176,220,0.20)' : 'rgba(14,114,184,0.18)';
      layer(0.15, 'background: linear-gradient(180deg, ' + (dark ? 'rgba(27,127,195,0.10)' : 'rgba(255,255,255,0.35)') + ', rgba(0,0,0,0) 50%), repeating-linear-gradient(0deg, ' + line2 + ' 0 2px, transparent 2px ' + 120 * u + 'px), repeating-linear-gradient(90deg, ' + line2 + ' 0 2px, transparent 2px ' + 120 * u + 'px), repeating-linear-gradient(0deg, ' + line1 + ' 0 1px, transparent 1px ' + 24 * u + 'px), repeating-linear-gradient(90deg, ' + line1 + ' 0 1px, transparent 1px ' + 24 * u + 'px), var(--bg);');
      const sheets = layer(0.5);
      const sheetCol = dark ? 'rgba(116,176,220,0.07)' : 'rgba(255,255,255,0.46)';
      const sheetBd = dark ? 'rgba(116,176,220,0.22)' : 'rgba(14,114,184,0.18)';
      const sheet = (cx, cy, w, h, rot) => div(sheets, 'abs', 'left:' + (X(cx) - w * u / 2) + 'px;top:' + (Y(cy) - h * u / 2) + 'px;width:' + w * u + 'px;height:' + h * u + 'px;transform:rotate(' + rot + 'deg);background:' + sheetCol + ';border:1px solid ' + sheetBd + ';border-radius:' + 6 * u + 'px;box-shadow:0 ' + 30 * u + 'px ' + 60 * u + 'px ' + (-24 * u) + 'px ' + shadowCol());
      sheet(1110, 480, 700, 540, 3);
      const top = sheet(1000, 420, 760, 580, -4);
      const ink = dark ? '#74B0DC' : '#0E72B8';
      top.innerHTML = '<svg viewBox="0 0 760 580" style="position:absolute;inset:0;width:100%;height:100%;overflow:visible">'
        + '<g transform="translate(160 60) scale(7)"><circle cx="32" cy="32" r="31" fill="none" stroke="' + ink + '" stroke-width="0.25" stroke-dasharray="1.4 1.1" style="animation: march 60s linear infinite"/>'
        + '<path d="M-6 17H70M-6 26H70M-6 52H70M12 -6V70M52 -6V70" fill="none" stroke="' + ink + '" stroke-width="0.18" stroke-dasharray="1 1" opacity="0.7"/>'
        + '<path d="' + BOX + '" fill="none" stroke="' + (dark ? '#EAF1F8' : '#0B3456') + '" stroke-width="0.9" stroke-linecap="round" stroke-linejoin="round"/>'
        + '<rect x="20" y="10" width="24" height="24" rx="4.5" transform="rotate(-8 32 22)" fill="none" stroke="' + p.gold + '" stroke-width="0.6" stroke-dasharray="1.4 0.9"/>'
        + '<path d="M12 60H52M12 58V62M52 58V62" fill="none" stroke="' + p.gold + '" stroke-width="0.35"/><text x="32" y="66.5" font-family="Figtree" font-size="3" font-weight="700" fill="' + p.gold + '" text-anchor="middle">40</text></g>'
        + '<g id="bpDraw"></g></svg>';
      const front = layer(1);
      div(front, 'abs', 'left:' + X(140) + 'px;top:' + Y(760) + 'px;width:' + 640 * u + 'px;height:' + 58 * u + 'px;transform:rotate(-8deg);border-radius:' + 6 * u + 'px;background:repeating-linear-gradient(90deg, rgba(11,52,86,0.55) 0 1px, transparent 1px ' + 16 * u + 'px) 0 0 / 100% 34% no-repeat, linear-gradient(180deg,' + lighten(p.gold, dark ? 0.08 : 0.3) + ',' + p.gold + ');box-shadow:inset 0 ' + 2 * u + 'px 0 rgba(255,255,255,0.4), 0 ' + 18 * u + 'px ' + 26 * u + 'px ' + (-10 * u) + 'px ' + shadowCol());
      const drawG = top.querySelector('#bpDraw');
      every(BEAT_MS, () => {
        if (drawG.childNodes.length > 6) return;
        const horiz = Math.random() < 0.5, x = rand(60, 640), y = rand(60, 520), len = rand(110, 240);
        const d = horiz ? 'M' + x + ' ' + y + 'H' + Math.min(740, x + len) : 'M' + x + ' ' + y + 'V' + Math.min(560, y + len);
        const g = svgAdd(drawG, 'g', {});
        const path = svgAdd(g, 'path', { d, fill: 'none', stroke: p.gold, 'stroke-width': 3, 'stroke-linecap': 'round', pathLength: 100, 'stroke-dasharray': 100, 'stroke-dashoffset': 100 });
        animate(path, [{ strokeDashoffset: 100 }, { strokeDashoffset: 0 }], { duration: 1100, easing: 'cubic-bezier(.45,0,.2,1)', fill: 'forwards' });
        later(6500, () => {
          const a = animate(g, [{ opacity: 1 }, { opacity: 0 }], { duration: 900, fill: 'forwards' });
          if (a) a.onfinish = () => g.remove(); else g.remove();
        });
      });
    },

    routes() {
      const p = P();
      const land = dark ? '#0E2A45' : '#F4F8FB', land2 = dark ? '#0B2238' : '#E7F0F7';
      const route = dark ? '#1B7FC3' : '#9CC5E6', edge = dark ? 'rgba(116,176,220,0.18)' : 'rgba(255,255,255,0.9)';
      layer(0.15, 'background: radial-gradient(ellipse 70% 60% at 70% 30%, ' + (dark ? 'rgba(27,127,195,0.12)' : 'rgba(255,255,255,0.55)') + ', rgba(0,0,0,0) 70%), var(--bg);');
      const lands = svgLayer(0.5, '<path d="M760 80C980 20 1320 60 1420 240C1500 400 1380 520 1200 560C1020 600 880 520 820 400C760 280 640 140 760 80Z" fill="' + land + '" stroke="' + edge + '" stroke-width="3"/>'
        + '<path d="M900 700C1040 640 1260 660 1380 760C1460 830 1400 920 1240 920C1080 920 940 900 880 830C840 780 850 720 900 700Z" fill="' + land2 + '" stroke="' + edge + '" stroke-width="3"/>'
        + '<path d="M-40 520C120 460 300 500 360 600C420 700 330 820 180 840C40 860 -60 780 -80 680Z" fill="' + land2 + '" stroke="' + edge + '" stroke-width="3"/>');
      lands.firstChild.style.filter = 'drop-shadow(0 ' + 22 * u + 'px ' + 26 * u + 'px ' + shadowCol() + ')';
      const paths = ['M180 760C420 600 560 820 820 640S1180 420 1340 220', 'M620 120C760 260 900 180 1040 320S1260 700 1400 760', 'M820 640C900 540 960 420 1040 320', 'M200 640C320 560 480 520 620 420'];
      const pins = [[820, 640, p.blue], [1340, 220, p.gold], [620, 120, p.blue], [1040, 320, p.gold], [1400, 760, p.blue], [200, 640, p.gold]];
      const rl = svgLayer(0.8, paths.map(d => '<path d="' + d + '" fill="none" stroke="' + route + '" stroke-width="6" stroke-linecap="round" stroke-dasharray="1 18"/>').join('')
        + paths.map((d, i) => '<path d="' + d + '" pathLength="1000" fill="none" stroke="' + p.gold + '" stroke-width="16" stroke-linecap="round" stroke-dasharray="1 999" style="animation: travel ' + [11, 14, 8, 12][i] + 's linear infinite; animation-delay: -' + [0, 5, 2, 7][i] + 's"/>').join('')
        + pins.map((q, i) => '<g><circle cx="' + q[0] + '" cy="' + q[1] + '" r="16" fill="none" stroke="' + q[2] + '" stroke-width="4" opacity="0" data-ring="' + i + '" style="transform-box: fill-box; transform-origin: center"/><circle cx="' + q[0] + '" cy="' + q[1] + '" r="16" fill="' + q[2] + '" stroke="' + (dark ? '#07182A' : '#FFFFFF') + '" stroke-width="6"/></g>').join(''));
      rl.firstChild.style.filter = 'drop-shadow(0 ' + 6 * u + 'px ' + 8 * u + 'px ' + shadowCol() + ')';
      const rings = Array.from(rl.querySelectorAll('[data-ring]'));
      const pulse = r => (r ? animate(r, [{ transform: 'scale(1)', opacity: 0.9 }, { transform: 'scale(3.2)', opacity: 0 }], { duration: 1400, easing: 'cubic-bezier(.22,1,.36,1)' }) : null);
      const truckL = layer(0.8);
      const roadPath = rl.querySelector('path');
      const L = roadPath && roadPath.getTotalLength ? roadPath.getTotalLength() : 0;
      let driving = false;
      const myBuild = buildId;
      function drive() {
        if (!L || !win.requestAnimationFrame) return;
        driving = true;
        const truck = makeTruck(truckL);
        const dur = 17000, t0 = (win.performance || Date).now();
        pulse(rings[5] || rings[0]);
        const step = now => {
          if (myBuild !== buildId || stopped) { truck.remove(); return; }
          const t = Math.min(1, (now - t0) / dur);
          const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
          const p1 = roadPath.getPointAtLength(e * L), p2 = roadPath.getPointAtLength(Math.min(L, e * L + 6));
          const dx = p2.x - p1.x, dy = p2.y - p1.y;
          const flip = dx < 0;
          let ang = Math.atan2(dy, Math.abs(dx)) * 180 / Math.PI;
          ang = Math.max(-28, Math.min(28, ang));
          const pop = t < 0.04 ? t / 0.04 : t > 0.96 ? (1 - t) / 0.04 : 1;
          truck.style.transform = 'translate(' + (X(p1.x) - truck.size.w / 2) + 'px,' + (Y(p1.y) - truck.size.h * 0.9) + 'px) rotate(' + (flip ? -ang : ang) + 'deg) scale(' + (flip ? -pop : pop) + ',' + pop + ')';
          if (t < 1) win.requestAnimationFrame(step);
          else { truck.remove(); driving = false; pulse(rings[1] || rings[0]); }
        };
        win.requestAnimationFrame(step);
      }
      every(BEAT_MS, () => {
        if (!driving && Math.random() < 0.3) drive();
        else pulse(pick(rings));
      });
      if (!reduceMotion) later(1500, drive);
    },

    barcode() {
      layer(0.15, 'background: linear-gradient(180deg, ' + (dark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)') + ', rgba(0,0,0,0) 50%), var(--bg);');
      const back = layer(0.45), front = layer(0.9);
      const tones = dark ? ['#1C2B3B', '#243546', '#2E4155'] : ['#C9D6E2', '#B3C3D3', '#9FB2C4'];
      const bars = [];
      let x = 80;
      while (x < 1480) {
        const w = pick([10, 14, 18, 26, 36, 48]);
        const near = Math.random() < 0.4;
        const col = pick(tones);
        const el = div(near ? front : back, 'abs lift-bar', 'left:' + X(x) + 'px;top:' + Y(near ? 90 : 70) + 'px;width:' + w * u + 'px;height:' + (near ? 700 : 760) * u + 'px;border-radius:' + 6 * u + 'px;background:linear-gradient(90deg,' + lighten(col, dark ? 0.06 : 0.35) + ',' + col + ' 45%,' + darken(col, 0.1) + ');box-shadow:0 ' + (near ? 26 : 14) * u + 'px ' + (near ? 34 : 22) * u + 'px ' + (-10 * u) + 'px ' + shadowCol());
        el.dataset.near = near ? 1 : 0;
        bars.push(el);
        x += w + pick([10, 14, 20, 28]);
      }
      const gold = dark ? '#D6961A' : '#EDAF24';
      div(front, 'abs', 'left:' + X(40) + 'px;right:0;top:' + Y(120) + 'px;height:' + 5 * u + 'px;border-radius:99px;background:' + gold + ';box-shadow:0 0 ' + 18 * u + 'px ' + 4 * u + 'px ' + (dark ? 'rgba(214,150,26,0.45)' : 'rgba(237,175,36,0.55)') + ';--sweep:' + 600 * u + 'px;animation: sweep 14s ease-in-out infinite alternate');
      every(BEAT_MS, () => {
        const i = Math.floor(Math.random() * (bars.length - 3));
        const group = bars.slice(i, i + 3);
        group.forEach(b => { b.style.transform = 'translateY(' + (-12 * u) + 'px)'; });
        later(1500, () => group.forEach(b => { b.style.transform = ''; }));
      });
    },

    topo() {
      layer(0.15, 'background: radial-gradient(ellipse 60% 55% at 75% 30%, ' + (dark ? 'rgba(27,127,195,0.10)' : 'rgba(255,255,255,0.6)') + ', rgba(0,0,0,0) 70%), var(--bg);');
      const blob = 'M0 -180C110 -190 210 -110 200 0C190 120 90 200 -20 190C-140 180 -210 90 -200 -20C-190 -120 -110 -170 0 -180Z';
      const shades = dark
        ? ['#0B2238', '#0D2A45', '#0F3354', '#123C63', '#154673', '#185083', '#1B5A92']
        : ['#E3EEF6', '#D5E6F3', '#C6DDF0', '#B3D2EB', '#9FC7E6', '#89BAE0', '#72ABD8'];
      const terrain = (cx, cy, s) => shades.map((c, k) => '<path d="' + blob + '" transform="translate(' + cx + ' ' + cy + ') rotate(' + k * 11 + ') scale(' + s * (1 - k * 0.13) + ')" fill="' + c + '" filter="url(#tsh)"/>').join('');
      const tl = svgLayer(0.55, '<defs><filter id="tsh" x="-30%" y="-30%" width="160%" height="160%"><feDropShadow dx="0" dy="10" stdDeviation="10" flood-color="' + (dark ? '#000' : '#0B3456') + '" flood-opacity="' + (dark ? 0.55 : 0.16) + '"/></filter></defs>'
        + terrain(1120, 300, 2.5) + terrain(170, 830, 1.25));
      const terraces = Array.from(tl.querySelectorAll('path')).filter((pth, i) => i % 7 > 0);
      terraces.forEach(pth => { pth.style.transformBox = 'fill-box'; pth.style.transformOrigin = 'center'; });
      every(BEAT_MS, () => {
        const pth = pick(terraces);
        animate(pth, [
          { translate: '0 0', filter: 'url(#tsh) brightness(1)' },
          { translate: '0 -9px', filter: 'url(#tsh) brightness(' + (dark ? 1.12 : 1.04) + ')', offset: 0.45 },
          { translate: '0 0', filter: 'url(#tsh) brightness(1)' },
        ], { duration: 2400, easing: 'cubic-bezier(.45,0,.2,1)' });
      });
    },

    horizon() {
      const p = P();
      layer(0.1, 'background: linear-gradient(180deg, ' + (dark ? '#0B1626 0%, #13233A 55%, #1A2E45 100%' : '#F7EEDC 0%, #F6F2E8 45%, #EAF3FA 100%') + ');');
      const sunL = layer(0.25);
      const sunS = 300 * u;
      div(sunL, 'abs', 'left:' + (X(1090) - sunS / 2) + 'px;top:' + (Y(430) - sunS / 2) + 'px;width:' + sunS + 'px;height:' + sunS + 'px;border-radius:50%;background:radial-gradient(circle at 40% 35%,' + lighten(p.gold, 0.3) + ',' + p.gold + ' 60%,' + darken(p.gold, 0.08) + ');box-shadow:0 0 ' + 80 * u + 'px ' + 20 * u + 'px ' + (dark ? 'rgba(214,150,26,0.25)' : 'rgba(237,175,36,0.35)') + ';--riseFrom:' + 80 * u + 'px;--riseTo:' + (-30 * u) + 'px;animation: rise 26s ease-in-out infinite alternate');
      const cloudsL = layer(0.35);
      [[120, 70, 22, 0.9], [300, 54, 70, -30], [200, 44, 48, -12]].forEach((c, i) => {
        const w = c[0] * 2 * u, h = c[1] * u;
        const cl = div(cloudsL, 'abs', 'left:0;top:' + Y(120 + i * 90) + 'px;width:' + w + 'px;height:' + h + 'px;animation: cloud ' + (70 + i * 25) + 's linear infinite;animation-delay:' + c[3] + 's');
        div(cl, 'abs', 'inset:0;border-radius:999px;background:' + (dark ? 'rgba(116,176,220,0.10)' : 'rgba(255,255,255,0.9)') + ';box-shadow:0 ' + 10 * u + 'px ' + 20 * u + 'px ' + (dark ? 'rgba(0,0,0,0.2)' : 'rgba(11,52,86,0.08)'));
        div(cl, 'abs', 'left:22%;top:-45%;width:40%;height:110%;border-radius:50%;background:' + (dark ? 'rgba(116,176,220,0.10)' : 'rgba(255,255,255,0.9)'));
      });
      const hc = dark ? ['#0B3456', '#0A4A77', '#0B5E98', '#1B7FC3'] : ['#D3E6F4', '#B9D5EC', '#9CC5E6', '#5FA3D6'];
      svgLayer(0.6, '<defs><filter id="hsh" x="-10%" y="-40%" width="120%" height="160%"><feDropShadow dx="0" dy="-8" stdDeviation="12" flood-color="' + (dark ? '#000' : '#0B3456') + '" flood-opacity="' + (dark ? 0.5 : 0.14) + '"/></filter></defs>'
        + '<path d="M-60 540C240 470 420 500 640 540S1080 450 1500 500V960H-60Z" fill="' + hc[0] + '" filter="url(#hsh)"/>'
        + '<path d="M-60 640C300 590 520 630 760 670S1180 590 1500 630V960H-60Z" fill="' + hc[1] + '" filter="url(#hsh)"/>');
      svgLayer(0.85, '<defs><filter id="hsh2" x="-10%" y="-40%" width="120%" height="160%"><feDropShadow dx="0" dy="-8" stdDeviation="12" flood-color="' + (dark ? '#000' : '#0B3456') + '" flood-opacity="' + (dark ? 0.5 : 0.16) + '"/></filter></defs>'
        + '<path d="M-60 760C260 725 560 760 820 790S1220 730 1500 750V960H-60Z" fill="' + hc[2] + '" filter="url(#hsh2)"/>'
        + '<path d="M-60 850C400 820 800 860 1500 830V960H-60Z" fill="' + hc[3] + '" filter="url(#hsh2)"/>');
    },

    pattern() {
      const p = P();
      layer(0.15, 'background: linear-gradient(180deg, ' + (dark ? 'rgba(27,127,195,0.08)' : 'rgba(255,255,255,0.45)') + ', rgba(0,0,0,0) 50%), var(--bg);');
      const L = layer(0.7);
      const items = [];
      const tile = 170;
      for (let r = -1; r < 7; r++) {
        for (let c = -1; c < 10; c++) {
          const cx = c * tile + 60 + (r % 2 ? tile / 2 : 0), cy = r * tile + 60;
          // about one in four large items is gold, spread across the screen
          const gold = ((r * 2 + c + 8) % 4 === 0) ? Math.random() < 0.75 : Math.random() < 0.08;
          const big = chip(L, cx, cy, gold ? 52 : 50, gold ? p.gold : (dark ? '#0F3A60' : '#D7E7F3'), rand(-12, 12), {});
          if (!reduceMotion) {
            big.style.animation = 'wander' + (1 + Math.floor(Math.random() * 3)) + ' ' + rand(7, 13) + 's ease-in-out infinite';
            big.style.animationDelay = -rand(0, 13) + 's';
          }
          items.push(big);
          const smGold = Math.random() < 0.12;
          const sm = chip(L, cx + tile * 0.48, cy + tile * 0.46, 32, smGold ? p.gold2 : (dark ? '#0C3050' : '#C6DDF0'), rand(-12, 12), {});
          if (!reduceMotion) {
            sm.style.animation = 'wander' + (1 + Math.floor(Math.random() * 3)) + ' ' + rand(7, 13) + 's ease-in-out infinite';
            sm.style.animationDelay = -rand(0, 13) + 's';
          }
          items.push(sm);
        }
      }
      every(BEAT_MS, () => pick(items).hop());
    },

    soft() {
      const p = P();
      layer(0.1, 'background: linear-gradient(180deg, ' + (dark ? 'rgba(234,241,248,0.03)' : 'rgba(255,255,255,0.6)') + ', rgba(0,0,0,0) 60%), var(--bg);');
      const L = layer(0.4);
      const shape = (cx, cy, s, rot, dur, delay) => {
        const S = s * u;
        const w = div(L, 'abs', 'left:' + (X(cx) - S / 2) + 'px;top:' + (Y(cy) - S / 2) + 'px;width:' + S + 'px;height:' + S + 'px;animation: breathe ' + dur + 's ease-in-out infinite alternate;animation-delay:' + delay + 's');
        div(w, 'abs', 'inset:0;border-radius:22%;transform:rotate(' + rot + 'deg);background:linear-gradient(160deg,' + (dark ? '#1A2C40' : '#FFFFFF') + ',' + (dark ? '#132335' : '#E3EAF1') + ');box-shadow:inset 0 ' + 2 * u + 'px 0 rgba(255,255,255,' + (dark ? 0.05 : 0.9) + '), 0 ' + 60 * u + 'px ' + 90 * u + 'px ' + (-30 * u) + 'px ' + (dark ? 'rgba(0,0,0,0.6)' : 'rgba(54,73,91,0.20)'));
      };
      shape(1270, 150, 560, -8, 14, 0);
      shape(970, 820, 420, 10, 18, -6);
      const F = layer(0.8);
      const g = chip(F, 1210, 590, 56, p.gold, -8, {});
      g.style.transition = 'left 2400ms cubic-bezier(.45,0,.2,1), top 2400ms cubic-bezier(.45,0,.2,1)';
      every(7800, () => {
        g.style.left = (X(rand(1000, 1380)) - 28 * u) + 'px';
        g.style.top = (Y(rand(420, 760)) - 28 * u) + 'px';
      });
    },

    plain() { layer(0, 'background: var(--bg);'); },
  };

  function build() {
    clearTimers();
    beats.length = 0;
    buildId += 1;
    host.innerHTML = '';
    W = win.innerWidth || 1440;
    H = win.innerHeight || 900;
    u = Math.max(W / 1440, H / 900);
    (THEMES[theme] || THEMES.plain)();
  }

  /* Hidden tab: no timers running and every animation held where it is. */
  function pause() {
    if (paused) return;
    paused = true;
    clearTimers();
    host.classList.add('is-paused');
  }
  function resume() {
    if (!paused) return;
    paused = false;
    host.classList.remove('is-paused');
    beats.forEach(b => timers.push(win.setInterval(b.fn, b.ms)));
  }
  const onVisibility = () => { if (doc.hidden) pause(); else resume(); };

  const onPointer = e => {
    if (reduceMotion || !parallax || e.pointerType === 'touch') return;
    const mx = e.clientX / (win.innerWidth || 1) - 0.5, my = e.clientY / (win.innerHeight || 1) - 0.5;
    host.querySelectorAll('.lyr').forEach(l => {
      const d = parseFloat(l.dataset.depth || 0);
      l.style.transform = 'translate(' + (-mx * PARALLAX.x * d) + 'px, ' + (-my * PARALLAX.y * d) + 'px)';
    });
  };
  const onResize = () => { win.clearTimeout(resizeTimer); resizeTimer = win.setTimeout(build, 200); };

  build();
  if (parallax && !reduceMotion) win.addEventListener('pointermove', onPointer);
  win.addEventListener('resize', onResize);
  doc.addEventListener('visibilitychange', onVisibility);
  if (doc.hidden) pause();

  return function stop() {
    stopped = true;
    buildId += 1;
    clearTimers();
    win.clearTimeout(resizeTimer);
    win.removeEventListener('pointermove', onPointer);
    win.removeEventListener('resize', onResize);
    doc.removeEventListener('visibilitychange', onVisibility);
    host.innerHTML = '';
    host.classList.remove('is-paused');
  };
}
